<template>
  <div class="high-risk">
    <div v-if="isVendorServiceEnded">
      <inline-warning-message>
        Unable to retrieve medical conditions from
        {{ excursionConfig?.student?.vendor?.name || 'vendor' }}. This service has ended and is no
        longer available. Medical information cannot be accessed through this integration. Please
        contact your school administrator to update your system integration and contact our support
        team or help transitioning to a supported provider.
      </inline-warning-message>
    </div>
    <inline-warning-message v-else-if="showMedicalConditionError">
      Failed to update student medical conditions!
    </inline-warning-message>
    <div v-if="studentMedicalConditions && 0 < studentMedicalConditions.length">
      <v-alert
        color="#ff7f32"
        variant="outlined"
        type="warning"
        icon="mdi-exclamation-thick"
        class="mb-3"
      >
        <div>
          The selected students for this excursion have the following medical conditions:
          <br /><br />
          <ul class="mb-0 pl-5 high-risk-student-medical-conditions">
            <li
              v-for="(medical, medicalIndex) in studentMedicalConditions"
              :key="medicalIndex"
              :class="{
                'high-risk-student-medical-supported': medical.supported,
                'text-deep-orange-lighten-1 font-weight-bold': medical.name.includes(
                  'Invalid Medical Condition'
                )
              }"
            >
              <v-icon
                v-if="medical.name.includes('Invalid Medical Condition')"
                icon="mdi-alert"
                size="small"
              />
              {{ medical.name }}
            </li>
          </ul>
        </div>
      </v-alert>
      <v-alert color="#c80063" icon="mdi-alert-circle" variant="outlined" class="mb-3">
        <div>
          Only the highlighted medical conditions are supported and available in the High-Risk
          Medical Conditions drop-down. For medical conditions that are not supported, please follow
          your School's process for managing student high-risk medical conditions.
        </div>
      </v-alert>
    </div>
    <stb-multi-select
      :id="objId"
      :select-options="getRiskClasses()"
      :disabled="!formControlEnabled"
      :placeholder="placeholder"
      :get-disabled-state="getDisabledState"
      @stb-multi-select-change="onMultiSelectChange"
    />
    <risk-class
      :selectedrisk-classes="selectedRiskClasses"
      :form-control-enabled="formControlEnabled"
      :risk-type-label-displayed="false"
      :has-updated-medical-condition="hasUpdatedMedicalCondition"
      class="pt-2"
      @change-risk-category-done="changeRiskCategory"
      @has-updated-medical-condition="hasUpdatedMedicalCondition = true"
    ></risk-class>
    <cs-form-dialog
      id="dlg-update-medical-condition-warning"
      v-model="warningUpdateMedicalConditionDialogDisplayed"
      body-id="dlg-body-update-medical-condition-warning"
      heading="Update Student Medical Condition"
      :primary-action="{
        label: 'Confirm',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="updateRiskClass"
      @secondary1-click="warningUpdateMedicalConditionDialogDisplayed = false"
      @update:model-value="warningUpdateMedicalConditionDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        This action may populate a new checklist. Do you want to continue?
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
// TODO: Use dependency injections instead of directly importing dependencies
import { cloneDeep, find } from 'lodash';
import RiskClass from './RiskClass';
import StbMultiSelect from './StbMultiSelect';

export default {
  name: 'HighRisk',

  components: {
    RiskClass,
    StbMultiSelect
  },

  props: {
    name: {
      type: String,
      required: false,
      default: ''
    },
    id: {
      type: [String, Number],
      required: false,
      default: ''
    },
    formControlEnabled: {
      type: Boolean,
      required: true
    },
    excursionConfig: {
      type: Object,
      default: () => ({
        student: { vendor: { isServiceEnded: false } }
      })
    }
  },
  emits: ['changeRiskCategoryDone'],

  data() {
    return {
      warningUpdateMedicalConditionDialogDisplayed: false,
      selectedRiskClass: '',
      selectedRiskTypeName: '',
      hasUpdatedMedicalCondition: false,
      isVendorServiceEnded: false
    };
  },

  computed: {
    placeholder() {
      return `Add ${this.name} Type`;
    },
    objId() {
      return `selectControl${this.id}`;
    },
    categoryName() {
      return this.name;
    },
    selectedRiskClasses() {
      return this.$store.getters['common/selectedClassByCategoryName'](this.name);
    },
    riskTypes() {
      return this.$store.getters['common/getListRiskTypesByCategoryName'](this.name);
    },
    studentMedicalConditions() {
      return this.$store.state.common.excursionDc.computed.studentMedicalConditions;
    },
    getDisabledState() {
      return (item) => {
        if (!item?.raw) return false;
        const riskParent = item.raw.subRiskType;
        const riskOption = item.raw.value;
        if (
          !this.selectedRiskClasses ||
          this.selectedRiskClasses.length === 0 ||
          !this.selectedRiskClasses[0].types
        ) {
          return false;
        }

        return this.selectedRiskClasses[0].types.some((riskType) => {
          return riskType.name === riskParent && find(riskType.classes, { name: riskOption });
        });
      };
    },
    showMedicalConditionError() {
      return !!this.$store.state.common.excursionStudentMedicalConditionsError?.error;
    }
  },
  watch: {
    excursionConfig: {
      handler(newValue) {
        this.isVendorServiceEnded = newValue?.student?.vendor?.isServiceEnded || false;
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    changeRiskCategory() {
      this.$emit('changeRiskCategoryDone', this.name);
    },
    getRiskClasses() {
      const riskClasses = [];
      if (this.riskTypes && this.riskTypes.length > 0) {
        this.riskTypes.forEach((t) => {
          if (t.classes && t.classes.length > 0) {
            const typeClasses = t.classes.map((c) => {
              const localC = cloneDeep(c);
              return {
                title: localC.name,
                value: localC.name,
                subRiskType: t.name
              };
            });
            typeClasses.sort((a, b) => a.title.localeCompare(b.title));

            riskClasses.push({ type: 'subheader', title: t.name, disabled: true });
            riskClasses.push(...typeClasses);
          }
        });
      }
      return riskClasses;
    },
    onMultiSelectChange(item) {
      this.selectedRiskClass = item.value;
      this.selectedRiskTypeName = item.subRiskType;

      if (this.selectedRiskClasses.length > 0 && !this.hasUpdatedMedicalCondition) {
        this.warningUpdateMedicalConditionDialogDisplayed = true;
        return;
      }

      this.updateRiskClass();
    },
    updateRiskClass() {
      this.$store.dispatch('common/changeSelectRiskClass', {
        riskClass: {
          name: this.selectedRiskClass
        },
        riskTypeName: this.selectedRiskTypeName,
        riskCategory: {
          name: this.categoryName
        }
      });
      this.selectedRiskClass = '';
      this.selectedRiskTypeName = '';
      this.hasUpdatedMedicalCondition = true;
      this.warningUpdateMedicalConditionDialogDisplayed = false;
      this.changeRiskCategory();
    }
  }
};
</script>

<style scoped>
.high-risk-student-medical-supported {
  color: #c80063;
}
</style>
